export interface ProjectionLot {
  expiresOnIso: string
  units: number
}

/**
 * Résultats déjà calculés de `addDaysIso`, par date puis décalage : les simulations du pilotage
 * l'appellent des centaines de milliers de fois sur quelques dizaines de dates. Vidé au-delà de
 * `ADD_DAYS_CACHE_MAX` dates.
 */
const addDaysCache = new Map<string, Map<number, string>>()
const ADD_DAYS_CACHE_MAX = 4096

export const addDaysIso = (iso: string, days: number): string => {
  const byOffset = addDaysCache.get(iso)
  const known = byOffset?.get(days)
  if (known !== undefined) return known
  const [year, month, day] = iso.split('-').map(Number)
  const result = new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
  if (byOffset) byOffset.set(days, result)
  else {
    if (addDaysCache.size >= ADD_DAYS_CACHE_MAX) addDaysCache.clear()
    addDaysCache.set(iso, new Map([[days, result]]))
  }
  return result
}

/** Jours ISO de `fromIso` à `toIso` inclus, vide si `toIso` précède `fromIso`. */
export const daysBetweenIso = (fromIso: string, toIso: string): string[] => {
  const days: string[] = []
  for (let day = fromIso; day <= toIso; day = addDaysIso(day, 1)) days.push(day)
  return days
}

/** Jours de `fromIso` à `toIso` (dates ISO, heure ignorée), négatif si `toIso` précède. */
export const dayDiffIso = (fromIso: string, toIso: string): number =>
  Math.round(
    (Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) -
      Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) /
      86_400_000,
  )

export const totalLotUnits = (lots: readonly ProjectionLot[]): number =>
  lots.reduce((sum, lot) => sum + lot.units, 0)

export const addLot = (lots: readonly ProjectionLot[], lot: ProjectionLot): ProjectionLot[] =>
  lot.units <= 0
    ? [...lots]
    : [...lots, lot].sort((a, b) => a.expiresOnIso.localeCompare(b.expiresOnIso))

export const sellFifo = (
  lots: readonly ProjectionLot[],
  units: number,
  dayIso: string,
): { lots: ProjectionLot[]; sold: number } => {
  const wanted = Math.max(0, units)
  let remaining = wanted
  const next: ProjectionLot[] = []
  for (const lot of lots) {
    if (remaining > 0 && lot.expiresOnIso > dayIso) {
      const taken = Math.min(lot.units, remaining)
      remaining -= taken
      if (lot.units - taken > 0) next.push({ ...lot, units: lot.units - taken })
    } else {
      next.push(lot)
    }
  }
  return { lots: next, sold: wanted - remaining }
}

export const expireLots = (
  lots: readonly ProjectionLot[],
  dayIso: string,
): { lots: ProjectionLot[]; expired: number } => {
  const limit = addDaysIso(dayIso, 1)
  const kept = lots.filter((lot) => lot.expiresOnIso > limit)
  return { lots: kept, expired: totalLotUnits(lots) - totalLotUnits(kept) }
}

export const shortDlcUnits = (lots: readonly ProjectionLot[], dayIso: string): number => {
  const limit = addDaysIso(dayIso, 1)
  return totalLotUnits(lots.filter((lot) => lot.expiresOnIso <= limit))
}
